import { Color, Shade } from './types';

export const HOVER_BG_COLOR = 'hoverBgColor';
export const HOVER_TEXT_COLOR = 'hoverTextColor';
export const DEFAULT = 'default';
export const DEFAULT_TEXT_COLOR = 'textColor';
export const LINK_TEXT_COLOR = 'linkTextColor';
export const AVATAR_BG_COLOR = 'avatarBgColor';
export const HIGHLIGHT_TEXT_COLOR = 'highlightTextColor';

type ColorMap = Map<Shade, string>;

const PRIMARY_COLORS: ColorMap = new Map([
    ['50', '#FFF9F8'],
    ['100', '#FFF1EE'],
    ['200', '#FEE4DE'],
    ['300', '#F4D3CC'],
    ['400', '#F6CAC1'],
    ['500', '#EE654C'],
    ['600', '#BE4D38'],
    ['700', '#933827'],
    ['800', '#48150C'],
    ['900', '#02020A'],
    ['highlightTextColor', '#EE654C'],
]);

const SECONDARY_COLORS: ColorMap = new Map([
    ['50', '#F2F7FF'],
    ['100', '#EBF3FF'],
    ['200', '#D3E5FF'],
    ['300', '#A7CBFF'],
    ['400', '#8CB9FC'],
    ['500', '#4D6D9B'],
    ['600', '#375480'],
    ['700', '#2E4B77'],
    ['800', '#243F69'],
    ['900', '#142A4A'],
]);

const SUCCESS_COLORS: ColorMap = new Map([
    ['50', '#F0FDF4'],
    ['100', '#DCFCE7'],
    ['200', '#BBF7D1'],
    ['300', '#86EFAC'],
    ['400', '#4ADE80'],
    ['500', '#23C55E'],
    ['600', '#17A34A'],
    ['700', '#157F3D'],
    ['800', '#166535'],
    ['900', '#15532E'],
]);

const WARNING_COLORS: ColorMap = new Map([
    ['50', '#FEFBEA'],
    ['100', '#FEF3C6'],
    ['200', '#FDE689'],
    ['300', '#FBD34D'],
    ['400', '#FBBF23'],
    ['500', '#FBBF23'],
    ['600', '#D87706'],
    ['700', '#B5530A'],
    ['800', '#92400E'],
    ['900', '#793510'],
    [DEFAULT, '#793510'],
]);

const ERROR_COLORS: ColorMap = new Map([
    ['50', '#FDF1F1'],
    ['100', '#FEE2E1'],
    ['200', '#FECBCA'],
    ['300', '#FBA5A4'],
    ['400', '#F77170'],
    ['500', '#EE4444'],
    ['600', '#DC2625'],
    ['700', '#B81C1D'],
    ['800', '#991B1C'],
    ['900', '#7F1D1C'],
]);

const SPECIAL_COLORS: ColorMap = new Map([
    ['50', '#FDF2F8'],
    ['100', '#FDE7F3'],
    ['200', '#FBCFE8'],
    ['300', '#F9A8D3'],
    ['400', '#F473B6'],
    ['500', '#EB4899'],
    ['600', '#DA2778'],
    ['700', '#BD185C'],
    ['800', '#9D174C'],
    ['900', '#811942'],
]);

const GREY_COLORS: ColorMap = new Map([
    ['0', '#FFFFFF'],
    ['50', '#F8FAFC'],
    ['100', '#F2F5F9'],
    ['200', '#E3E8EF'],
    ['300', '#D1D8E2'],
    ['400', '#9AA5B8'],
    ['500', '#677489'],
    ['600', '#4A5567'],
    ['700', '#384150'],
    ['800', '#29303E'],
    ['900', '#1B202D'],
    [DEFAULT, '#FFFFFF'],
]);

const NO_COLORS = new Map([['0', 'none']]);

type Colors = Map<Color, ColorMap>;

const COLORS: Colors = new Map([
    ['primary', PRIMARY_COLORS as ColorMap],
    ['secondary', SECONDARY_COLORS as ColorMap],
    ['success', SUCCESS_COLORS as ColorMap],
    ['warning', WARNING_COLORS as ColorMap],
    ['error', ERROR_COLORS as ColorMap],
    ['special', SPECIAL_COLORS as ColorMap],
    ['grey', GREY_COLORS as ColorMap],
    ['none', NO_COLORS as ColorMap],
]);

const THEMES: Record<string, string> = {
    defaultTheme: 'grey',
    defaultShade: '900',
};

export const getColor = (
    theme: Color = THEMES.defaultTheme as Color,
    shade: Shade = THEMES.defaultShade as Shade
): string => {
    const color: ColorMap = COLORS.get(theme) || PRIMARY_COLORS;
    return color.get(shade) || 'none';
};

export const getColorValues = (variant: Color, start: number, end: number, sort = 'asc') => {
    const colors = COLORS.get(variant) || PRIMARY_COLORS;
    const array = Array.from(colors.values());
    const result = array.filter((el: string, indx: number) => indx + 1 >= start && indx < end);
    return sort === 'desc' ? result.reverse() : result;
};

type AvatarStyle = {
    background: string;
    text: string;
    type: string;
    bgColor: string;
    textColor: string;
};

export const getSequentialArrVal = (source: AvatarStyle[], index: number, reverse = false) => {
    const arr = reverse ? source.reverse() : source;
    if (arr[index]) {
        return arr[index];
    }
    const indexVal = index % arr.length;
    return arr[indexVal];
};

export const getAvatarColor = (index = Math.floor(Math.random() * 10) + 1) => {
    const defaultBackgroundShade = '400';
    const defaultTextShade = '100';
    const avatarArr: AvatarStyle[] = [];
    COLORS.forEach((value: ColorMap, key: Color) => {
        const obj = {
            background: '',
            text: '',
            type: key,
            bgColor: defaultBackgroundShade,
            textColor: defaultTextShade,
        };
        if (key !== 'none' && key !== 'grey') {
            obj.background = `${value.get(defaultBackgroundShade)}`;
            obj.text = `${value.get(defaultTextShade)}`;
            avatarArr.push(obj);
        }
    });
    return getSequentialArrVal(avatarArr, index);
};
